<template>
  <div class="schedule-container" :style="{ backgroundColor: backgroundColor }">
    <div class="text-subtitle-2 mb-2">
      <div class="d-flex justify-space-between">
        <span>
          <a @click="lookupSchedule">
            {{ schedule.studentFirstName }} {{ schedule.studentLastName }}
          </a>
          <span v-if="!globalCenter || !globalCenter.id">
            @ {{ getCenterName(schedule.centerId) }}
          </span>
        </span>
        <span> <!-- placeholder --> </span>
      </div>
    </div>
    <div>
      {{ schedule.startTime | formatDate }},
      {{ schedule.startTime | formatTime }} -
      {{ schedule.endTime | formatTime }}
    </div>

    <div v-if="schedule.partOfRecurring">
      Weekly on {{ schedule.recurringDays.join(", ") }}
    </div>
    <div v-if="schedule.partOfRecurring">
      Since {{ schedule.recurringStartDate | formatDate }}
      {{
        schedule.recurringEndDate
          ? ` until ${$options.filters.formatDate(schedule.recurringEndDate)}`
          : " (no end date)"
      }}
    </div>

    <div class="my-2" v-if="schedule.programId">
      <div>Program: {{ getProgramName(schedule.programId) }}</div>

      <div
        class="d-flex justify-space-between"
        v-for="enrollment in enrollments"
        :key="enrollment.id"
      >
        <div>
          Enrollment:
          <span>
            {{ enrollment.startDate | formatShortDate }} -
            <span v-if="enrollment.endDate">
              {{ enrollment.endDate | formatShortDate }}
            </span>
            <span v-else> No end date </span>
          </span>
          <span v-if="enrollment.numberOfSessions">
            {{ enrollment.numberOfSessions }} Sessions
          </span>
        </div>
        <span v-if="enrollment.attendanceStatusGroup">
          <div
            v-for="(count, status) in enrollment.attendanceStatusGroup.count"
            :key="status"
          >
            <a @click="viewAttendances"> {{ count }} {{ status }} </a>
          </div>
        </span>
      </div>

      <!-- Display standalone attendance status if no enrollments are found -->
      <div
        class="d-flex justify-space-between"
        v-if="!enrollments || enrollments.length < 1"
      >
        <div>No enrollment found</div>
        <div
          v-for="(count, status) in standaloneAttendanceStatusGroup.count"
          :key="status"
        >
          <a @click="viewAttendances"> {{ count }} {{ status }} </a>
        </div>
      </div>
    </div>

    <div v-if="schedule.notes">
      <div>Notes:</div>
      <pre class="ml-2">{{ schedule.notes }} </pre>
    </div>
    <v-divider class="my-2" />
    <div class="d-flex justify-end">
      <div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              icon
              v-bind="attrs"
              v-on="on"
              @click="editSchedule"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Edit Schedule</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import dialogMixin from '@/components/schedule/dialogMixin.js'

import { mapActions, mapGetters } from "vuex";
import AttendanceService from "@/services/attendanceService.js";
import moment from "moment";
import {
  openStudentAttendance,
  openStudentScheduleLookup,
  openEditStudentScheduleInstance,
} from "@/components/schedule/eventHandler.js";

export default {
  mixins: [dialogMixin],
  props: {
    schedule: {
      type: Object,
      required: true,
    },
    teacherId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    programId2Name: {},
    enrollments: [], // Store each enrollment and its attendances
    standaloneAttendanceStatusGroup: {}, // Standalone attendances without enrollments
    attendanceData: {}, // Newly added to store attendance data
    isAttendanceDataLoaded: false, // Indicate whether attendanceData is fetched
  }),
  created() {
    this.loadPrograms(this.schedule.centerId);
    this.fetchStudentEnrollments(this.schedule);
  },
  computed: {
    ...mapGetters(["globalCenter", "myCenters", "getCenterName"]),
    teacher() {
      return this.teachers && this.teacherId
        ? this.teachers.find((t) => t.id == this.teacherId)
        : null;
    },
    backgroundColor() {
      if (!this.isAttendanceDataLoaded) {
        return "#ffffff";
      }

      const attendanceRecords = this.attendanceData;
      const scheduleDate = moment(this.schedule.startTime).format("YYYY-MM-DD");

      const attendanceStatus = attendanceRecords
        .filter((attendance) => {
          const attendanceDate = moment(attendance.attendedDate).format(
            "YYYY-MM-DD"
          );
          return (
            attendanceDate === scheduleDate &&
            attendance.studentScheduleId === this.schedule.id
          );
        })
        .map((attendance) => attendance.status.toLowerCase());

      // Set different background colors based on the attendance status
      if (attendanceStatus.includes("present")) {
        return "#c6e1c6"; // Color for "Present"
      } else if (attendanceStatus.includes("absent")) {
        return "#ffb3b3"; // Color for "Absent"
      } else if (attendanceStatus.includes("late")) {
        return "#fff8d3"; // Color for "Late"
      } else {
        return "#ffffff"; // Default color
      }
    },
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage", "clearMessage"]),
    editSchedule() {
      openEditStudentScheduleInstance({
        studentSchedule: this.schedule,
        teacher: this.teacher,
        parentID: this.uuID
      });
    },
    viewAttendances() {
      const student = this.getStudent();
      openStudentAttendance({ student, programIds: [this.schedule.programId] });
    },
    loadPrograms(center) {
      const params = { centerId: center };
      this.$axios.get("/program/get-by-center", { params }).then((resp) => {
        this.programId2Name = resp.data.reduce((acc, p) => {
          acc[p.id] = p.name;
          return acc;
        }, {});
      });
    },
    lookupSchedule() {
      const student = this.getStudent();
      openStudentScheduleLookup({ student });
    },
    getStudent() {
      const { studentId, studentFirstName, studentLastName } = this.schedule;
      return {
        id: studentId,
        firstName: studentFirstName,
        lastName: studentLastName,
      };
    },
    getProgramName(id) {
      return this.programId2Name[id];
    },
    async fetchStudentEnrollments(schedule) {
      const { studentId, programId } = schedule;
      this.$axios
        .get("/enrollment/by-student/" + studentId, {
          params: { program_id: programId },
        })
        .then(async (resp) => {
          if (resp && resp.data && resp.data.length > 0) {
            this.enrollments = resp.data;
            for (let i = 0; i < this.enrollments.length; i++) {
              let enrollment = this.enrollments[i];
              let startDate = enrollment.startDate;
              let endDate = enrollment.endDate || moment().format("YYYY-MM-DD");
              const attendance =
                await AttendanceService.fetchStudentAttendanceBySchedule(
                  this.schedule,
                  startDate,
                  endDate
                );
              this.$set(enrollment, "attendanceStatusGroup", attendance);
              // Save attendance data for later use in deleteSchedule function
              this.attendanceData = attendance.data;
              this.isAttendanceDataLoaded = true;
            }
          } else {
            // No enrollments found, fetch standalone attendances
            let startDate = "1970-01-01"; // start date is set to earliest possible date
            let endDate = moment().format("YYYY-MM-DD"); // end date is set to today's date
            const attendance =
              await AttendanceService.fetchStudentAttendanceBySchedule(
                this.schedule,
                startDate,
                endDate
              );
            this.standaloneAttendanceStatusGroup = attendance;
            // Save attendance data for later use in deleteSchedule function
            this.attendanceData = attendance.data;
            this.isAttendanceDataLoaded = true;
          }
        });
    },
  },
};
</script>

<style scoped>
.schedule-container {
  border: 1px solid #ccc;
  margin-bottom: 20px;
  padding: 20px;
}

.attendance-taken {
  background-color: #c6e1c6; /* Set your desired background color for when attendance is taken */
}
</style>
